import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, MapPin, Users } from "lucide-react";
import api from "@/lib/api";
import { Shell } from "@/components/Shell";

export default function Portal() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/events")
      .then(({ data }) => setEvents(data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Shell wide>
      <section className="rise" data-testid="portal-hero">
        <p className="overline">Live event credentials</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-3 max-w-3xl">
          Register once.<br />
          <span className="text-[#00e599]">Scan in</span> under two seconds.
        </h1>
        <p className="text-sm sm:text-base text-slate-400 mt-5 max-w-xl leading-relaxed">
          Self-service registration, a unique QR pass by email, on-site camera check-in with instant badge
          printing, and live attendance telemetry for organisers.
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/retrieve-qr" data-testid="hero-retrieve-btn" className="btn-primary">
            Find my QR pass
          </Link>
          <Link to="/moderator/login" data-testid="hero-organiser-btn" className="btn-secondary">
            I'm an organiser
          </Link>
        </div>
      </section>

      <section className="mt-16">
        <div className="flex items-end justify-between border-b border-[#252f3e] pb-3">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
            Open registrations
          </h2>
          <span className="font-mono2 text-xs text-slate-500">{events.length} event(s)</span>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 mt-8">Loading events…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-500 mt-8" data-testid="portal-empty">
            No published events yet.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-8" data-testid="portal-event-grid">
            {events.map((e, i) => {
              const pct = Math.min(100, Math.round((e.registered / Math.max(e.capacity, 1)) * 100));
              return (
                <Link
                  key={e.event_id}
                  to={`/e/${e.slug}`}
                  data-testid={`portal-event-card-${e.slug}`}
                  className="card overflow-hidden group hover:border-[#00e599]/40 transition-colors rise"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="h-40 bg-[#141a23] overflow-hidden">
                    {e.key_visual_url ? (
                      <img
                        src={e.key_visual_url}
                        alt={e.name}
                        className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                      />
                    ) : null}
                  </div>
                  <div className="p-5">
                    <p className="font-mono2 text-[11px] text-[#00e599] uppercase tracking-widest flex items-center gap-1.5">
                      <CalendarDays size={13} /> {e.date} {e.start_time}
                    </p>
                    <h3 className="text-xl font-bold mt-2 leading-snug group-hover:text-[#00e599] transition-colors">
                      {e.name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                      <MapPin size={13} /> {e.venue || "Venue TBA"}
                    </p>
                    <div className="mt-5">
                      <div className="h-1.5 rounded-full bg-[#1c2430] overflow-hidden">
                        <div className="h-full bg-[#00e599]" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="font-mono2 text-[11px] text-slate-400 mt-2 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Users size={12} /> {e.registered}/{e.capacity}
                        </span>
                        <span className="flex items-center gap-1 text-slate-300">
                          {e.spots_left > 0 ? `${e.spots_left} spots left` : "Waitlist"}
                          <ArrowUpRight size={12} />
                        </span>
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </Shell>
  );
}
